/**
 * The colour × size grid both printed documents use (revision item 20).
 *
 * A vendor order form is style-grouped: one block per style, colours down,
 * sizes across, alphabetical — which is why it reads at a glance. Both the
 * purchase order and the receiving document render this same shape over their
 * own lines, so the grouping lives here as a function rather than twice in
 * JSX.
 */

export interface GridLine {
  readonly productName: string;
  readonly styleNumber: string | null;
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  readonly sizeSort: number;
  readonly altSize: string | null;
  /** The maker's own number for the size, when the vendor's export carried one. */
  readonly vendorPartNumber?: string | null;
  readonly quantity: number;
  readonly unitCostCents: number;
  readonly extensionCents: number;
}

export interface GridRow {
  /** Colour, with the second size axis when the style uses one (decision 89). */
  readonly label: string;
  /** Quantity per size column, `null` where this colour has no such size. */
  readonly cells: (number | null)[];
  readonly units: number;
  readonly extensionCents: number;
  /** The one cost every unit on this row was priced at, or null when mixed. */
  readonly unitCostCents: number | null;
}

export interface StyleBlock {
  readonly name: string;
  readonly styleNumber: string | null;
  /**
   * The maker's numbers on this style's sizes, where they differ from the
   * style number — "the manufacturer may only know this number" (FB-1079).
   * Usually one per style; the import already uses it as the style number
   * when it can, in which case there is nothing more to print.
   */
  readonly mfrNumbers: readonly string[];
  /** Column labels, in `size_sort` order — XS before S, 2 before 10. */
  readonly sizes: string[];
  readonly rows: GridRow[];
  readonly units: number;
  readonly extensionCents: number;
}

const NO_SIZE = '—';

/**
 * Styles alphabetical, sizes in sort order, colours alphabetical within the
 * style. Every size column a style carries appears for every colour row, so
 * an empty cell means "not this colour", never "column missing".
 */
export function buildDocumentGrid(lines: readonly GridLine[]): StyleBlock[] {
  const styles = new Map<string, GridLine[]>();
  for (const line of lines) {
    const key = `${line.productName}|${line.styleNumber ?? ''}`;
    const bucket = styles.get(key);
    if (bucket === undefined) styles.set(key, [line]);
    else bucket.push(line);
  }

  return [...styles.values()]
    .map((styleLines) => {
      const first = styleLines[0]!;

      // Size columns: dedup by label, ordered by sizeSort (then label, so two
      // unaliased sizes that both sort to zero still land deterministically).
      const seen = new Map<string, number>();
      for (const line of styleLines) {
        const label = line.sizeLabel ?? NO_SIZE;
        if (!seen.has(label)) seen.set(label, line.sizeSort);
      }
      const sizes = [...seen.entries()]
        .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
        .map(([label]) => label);
      const column = new Map(sizes.map((label, index) => [label, index]));

      const rows = new Map<string, { cells: (number | null)[]; lines: GridLine[] }>();
      for (const line of styleLines) {
        const label = [line.colorName ?? NO_SIZE, line.altSize].filter(Boolean).join(' · ');
        let row = rows.get(label);
        if (row === undefined) {
          row = { cells: sizes.map(() => null), lines: [] };
          rows.set(label, row);
        }
        const index = column.get(line.sizeLabel ?? NO_SIZE)!;
        row.cells[index] = (row.cells[index] ?? 0) + line.quantity;
        row.lines.push(line);
      }

      const builtRows: GridRow[] = [...rows.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([label, row]) => {
          const units = row.lines.reduce((sum, line) => sum + line.quantity, 0);
          const extensionCents = row.lines.reduce((sum, line) => sum + line.extensionCents, 0);
          const costs = new Set(row.lines.map((line) => line.unitCostCents));
          return {
            label,
            cells: row.cells,
            units,
            extensionCents,
            // Cost varies by size within one style in this shop's real data
            // (SCOPE §3), so a single figure is only printed when it is true.
            unitCostCents: costs.size === 1 ? row.lines[0]!.unitCostCents : null,
          };
        });

      return {
        name: first.productName,
        styleNumber: first.styleNumber,
        mfrNumbers: [
          ...new Set(
            styleLines
              .map((line) => line.vendorPartNumber?.trim() ?? '')
              .filter((part) => part !== '' && part !== (first.styleNumber ?? '').trim()),
          ),
        ],
        sizes,
        rows: builtRows,
        units: builtRows.reduce((sum, row) => sum + row.units, 0),
        extensionCents: builtRows.reduce((sum, row) => sum + row.extensionCents, 0),
      };
    })
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name) || (a.styleNumber ?? '').localeCompare(b.styleNumber ?? ''),
    );
}
